import React from 'react';
import { Box, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from './SalovareSelectField';
import SwitchField from './SwitchField';
import { nativeInputSx, fieldLabelSx } from './formFieldStyles';
import { TAXABLE_CATEGORIES } from '../../utils/vatClassification';

const VatClassificationFields = ({
  value,
  onChange,
  errors = {},
  idPrefix = 'vat',
}) => {
  const { t } = useTranslation();
  const vatExempt = value?.vatExempt !== false;
  const taxCategory = value?.taxCategory || 'Exempt';

  const update = (patch) => {
    onChange({
      vatExempt,
      taxCategory,
      vatRate: value?.vatRate ?? '',
      vatExemptionReason: value?.vatExemptionReason ?? '',
      ...patch,
    });
  };

  const setExempt = (checked) => {
    if (checked) {
      update({ vatExempt: true, taxCategory: 'Exempt' });
      return;
    }

    update({
      vatExempt: false,
      taxCategory: taxCategory === 'Exempt' ? 'Normal' : taxCategory,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SwitchField
        id={`${idPrefix}-exempt`}
        checked={vatExempt}
        onChange={(event) => setExempt(event.target.checked)}
        label={t('vat.fields.exempt')}
        helpText={t('vat.help.exempt')}
      />

      {vatExempt ? (
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('vat.fields.reason')}
          </Typography>
          <Box
            component="input"
            id={`${idPrefix}-reason`}
            type="text"
            aria-label={t('vat.fields.reason')}
            value={value?.vatExemptionReason || ''}
            onChange={(event) => update({ vatExemptionReason: event.target.value })}
            sx={nativeInputSx(false)}
          />
        </Box>
      ) : (
        <>
          <SalovareSelectField
            id={`${idPrefix}-tax-category`}
            label={t('vat.fields.taxCategory')}
            value={taxCategory === 'Exempt' ? 'Normal' : taxCategory}
            onChange={(event) => update({ taxCategory: event.target.value })}
          >
            {TAXABLE_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {t(`vat.categories.${category}`)}
              </MenuItem>
            ))}
          </SalovareSelectField>

          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {`${t('vat.fields.rate')} *`}
            </Typography>
            <Box
              component="input"
              id={`${idPrefix}-rate`}
              type="number"
              min={0}
              max={100}
              step="0.01"
              aria-label={t('vat.fields.rate')}
              value={value?.vatRate ?? ''}
              onChange={(event) => update({ vatRate: event.target.value })}
              sx={nativeInputSx(Boolean(errors.vatRate))}
            />
            {errors.vatRate ? (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                {t(errors.vatRate)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {t('vat.help.rate')}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default VatClassificationFields;
